/**
 * Location Service for V2 Services
 */

import { FirestoreService } from '../db/FirestoreService';
import { Building, Apartment, BuildingData, ApartmentData } from '../models/Location.model';

export class LocationService {
  private firestore: FirestoreService;
  private readonly BUILDINGS_COLLECTION = 'buildings';
  private readonly APARTMENTS_COLLECTION = 'apartments';

  constructor() {
    this.firestore = FirestoreService.getInstance();
  }

  async createBuilding(buildingData: Partial<BuildingData>): Promise<Building> {
    const building = new Building(buildingData);
    const docRef = await this.firestore.addDocument(this.BUILDINGS_COLLECTION, building.toFirestore());
    building.id = docRef.id;
    return building;
  }

  async getBuilding(id: string): Promise<Building | null> {
    const doc = await this.firestore.getDocument(this.BUILDINGS_COLLECTION, id);
    return doc ? Building.fromFirestore({ id, ...doc }) : null;
  }

  async updateBuilding(id: string, updates: Partial<BuildingData>): Promise<void> {
    await this.firestore.updateDocument(this.BUILDINGS_COLLECTION, id, {
      ...updates,
      updatedAt: new Date()
    });
  }

  async deleteBuilding(id: string): Promise<void> {
    const apartments = await this.getApartmentsByBuilding(id);
    if (apartments.length > 0) {
      throw new Error('Cannot delete building with existing apartments');
    }
    await this.firestore.deleteDocument(this.BUILDINGS_COLLECTION, id);
  }

  async getAllBuildings(): Promise<Building[]> {
    const docs = await this.firestore.queryDocuments(
      this.BUILDINGS_COLLECTION,
      [],
      { field: 'name', direction: 'asc' }
    );
    return docs.map(doc => Building.fromFirestore(doc));
  }

  async searchBuildings(searchTerm: string): Promise<Building[]> {
    const docs = await this.firestore.queryDocuments(
      this.BUILDINGS_COLLECTION,
      [{ field: 'name', operator: '>=', value: searchTerm }]
    );
    return docs.map(doc => Building.fromFirestore(doc));
  }

  async createApartment(apartmentData: Partial<ApartmentData>): Promise<Apartment> {
    const apartment = new Apartment(apartmentData);
    const docRef = await this.firestore.addDocument(this.APARTMENTS_COLLECTION, apartment.toFirestore());
    apartment.id = docRef.id;
    return apartment;
  }

  async getApartment(id: string): Promise<Apartment | null> {
    const doc = await this.firestore.getDocument(this.APARTMENTS_COLLECTION, id);
    return doc ? Apartment.fromFirestore({ id, ...doc }) : null;
  }

  async updateApartment(id: string, updates: Partial<ApartmentData>): Promise<void> {
    await this.firestore.updateDocument(this.APARTMENTS_COLLECTION, id, {
      ...updates,
      updatedAt: new Date()
    });
  }

  async deleteApartment(id: string): Promise<void> {
    await this.firestore.deleteDocument(this.APARTMENTS_COLLECTION, id);
  }

  async getApartmentsByBuilding(buildingId: string): Promise<Apartment[]> {
    const docs = await this.firestore.queryDocuments(
      this.APARTMENTS_COLLECTION,
      [{ field: 'buildingId', operator: '==', value: buildingId }],
      { field: 'floor', direction: 'asc' }
    );
    return docs.map(doc => Apartment.fromFirestore(doc));
  }

  async getApartmentsByFloor(buildingId: string, floor: number): Promise<Apartment[]> {
    const docs = await this.firestore.queryDocuments(
      this.APARTMENTS_COLLECTION,
      [
        { field: 'buildingId', operator: '==', value: buildingId },
        { field: 'floor', operator: '==', value: floor }
      ],
      { field: 'apartmentNumber', direction: 'asc' }
    );
    return docs.map(doc => Apartment.fromFirestore(doc));
  }

  async getApartmentsByUser(userId: string): Promise<Apartment[]> {
    const docs = await this.firestore.queryDocuments(
      this.APARTMENTS_COLLECTION,
      [{ field: 'userId', operator: '==', value: userId }]
    );
    return docs.map(doc => Apartment.fromFirestore(doc));
  }

  async getUnassignedApartments(buildingId?: string): Promise<Apartment[]> {
    const filters = [{ field: 'userId', operator: '==', value: null }];
    if (buildingId) {
      filters.push({ field: 'buildingId', operator: '==', value: buildingId });
    }

    const docs = await this.firestore.queryDocuments(
      this.APARTMENTS_COLLECTION,
      filters
    );
    return docs.map(doc => Apartment.fromFirestore(doc));
  }

  async assignApartmentToUser(apartmentId: string, userId: string): Promise<void> {
    const apartment = await this.getApartment(apartmentId);
    if (!apartment) throw new Error('Apartment not found');

    apartment.assignToUser(userId);
    await this.updateApartment(apartmentId, apartment.toFirestore());
  }

  async unassignApartment(apartmentId: string): Promise<void> {
    await this.updateApartment(apartmentId, { userId: undefined });
  }

  async updateCleaningSchedule(apartmentId: string, lastCleaning: Date, nextCleaning: Date): Promise<void> {
    const apartment = await this.getApartment(apartmentId);
    if (!apartment) throw new Error('Apartment not found');

    apartment.updateCleaningSchedule(lastCleaning, nextCleaning);
    await this.updateApartment(apartmentId, apartment.toFirestore());
  }

  async getApartmentsDueForCleaning(daysFromNow: number = 0): Promise<Apartment[]> {
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + daysFromNow);

    const docs = await this.firestore.queryDocuments(
      this.APARTMENTS_COLLECTION,
      [{ field: 'nextCleaning', operator: '<=', value: targetDate }],
      { field: 'nextCleaning', direction: 'asc' }
    );
    return docs.map(doc => Apartment.fromFirestore(doc));
  }

  async getBuildingStats(buildingId: string): Promise<{
    totalApartments: number;
    assignedApartments: number;
    unassignedApartments: number;
    apartmentsPerFloor: Record<number, number>;
    lastCleaningActivity?: Date;
  }> {
    const apartments = await this.getApartmentsByBuilding(buildingId);

    const stats = apartments.reduce((acc, apartment) => {
      acc.totalApartments++;

      if (apartment.userId) {
        acc.assignedApartments++;
      } else {
        acc.unassignedApartments++;
      }

      acc.apartmentsPerFloor[apartment.floor] = (acc.apartmentsPerFloor[apartment.floor] || 0) + 1;

      if (apartment.lastCleaning) {
        if (!acc.lastCleaningActivity || apartment.lastCleaning > acc.lastCleaningActivity) {
          acc.lastCleaningActivity = apartment.lastCleaning;
        }
      }

      return acc;
    }, {
      totalApartments: 0,
      assignedApartments: 0,
      unassignedApartments: 0,
      apartmentsPerFloor: {} as Record<number, number>,
      lastCleaningActivity: undefined as Date | undefined
    });

    return stats;
  }

  async getAllStats(): Promise<{
    totalBuildings: number;
    totalApartments: number;
    assignedApartments: number;
    unassignedApartments: number;
    apartmentsDueCleaning: number;
  }> {
    const [buildings, allApartments, apartmentsDue] = await Promise.all([
      this.getAllBuildings(),
      this.firestore.queryDocuments(this.APARTMENTS_COLLECTION),
      this.getApartmentsDueForCleaning()
    ]);

    const apartments = allApartments.map(doc => Apartment.fromFirestore(doc));

    const assignedApartments = apartments.filter(apt => apt.userId).length;

    return {
      totalBuildings: buildings.length,
      totalApartments: apartments.length,
      assignedApartments,
      unassignedApartments: apartments.length - assignedApartments,
      apartmentsDueCleaning: apartmentsDue.length
    };
  }

  subscribeToBuildings(callback: (buildings: Building[]) => void): () => void {
    return this.firestore.subscribeToCollection(
      this.BUILDINGS_COLLECTION,
      (docs) => {
        const buildings = docs.map(doc => Building.fromFirestore(doc));
        callback(buildings);
      },
      [],
      { field: 'name', direction: 'asc' }
    );
  }

  subscribeToBuildingApartments(buildingId: string, callback: (apartments: Apartment[]) => void): () => void {
    return this.firestore.subscribeToCollection(
      this.APARTMENTS_COLLECTION,
      (docs) => {
        const apartments = docs.map(doc => Apartment.fromFirestore(doc));
        callback(apartments);
      },
      [{ field: 'buildingId', operator: '==', value: buildingId }],
      { field: 'floor', direction: 'asc' }
    );
  }

  subscribeToUserApartments(userId: string, callback: (apartments: Apartment[]) => void): () => void {
    return this.firestore.subscribeToCollection(
      this.APARTMENTS_COLLECTION,
      (docs) => {
        const apartments = docs.map(doc => Apartment.fromFirestore(doc));
        callback(apartments);
      },
      [{ field: 'userId', operator: '==', value: userId }]
    );
  }
}